import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/axios';
import '../admin.css';
import AdminWatermark from './AdminWatermark';

const SuperAdminTenantManagementPage = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newTenant, setNewTenant] = useState({ tenant_code: '', domain: '', name: '' });

  // Aggregation Job Queue admin state
  const [queueStatus, setQueueStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [maxWorkers, setMaxWorkers] = useState(1);

  // Event Worker admin state
  const [eventWorkerStatus, setEventWorkerStatus] = useState(null);
  const [eventWorkerStats, setEventWorkerStats] = useState(null);
  const [eventWorkerLoading, setEventWorkerLoading] = useState(false);
  const [eventWorkerConfig, setEventWorkerConfig] = useState({
    batch_size: 10,
    polling_interval: 5,
    retry_delay_minutes: 5
  });

  // Memory Trace admin state
  const [memoryTraceStatus, setMemoryTraceStatus] = useState(null);
  const [memoryTraceLoading, setMemoryTraceLoading] = useState(false);
  const [memoryTraceResults, setMemoryTraceResults] = useState([]);
  const [memoryTraceConfig, setMemoryTraceConfig] = useState({
    threshold_mb: 10,
    polling_interval: 60
  });
  const [selectedTraceback, setSelectedTraceback] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await apiClient.get('/iam/me');
      const data = response.data;
      const isSuperAdmin = data.role === 'admin' && data.tenant_code === 'admin';
      if (!isSuperAdmin) {
        setError('Super admin access required');
        setTimeout(() => navigate('/dashboard'), 2500);
        return false;
      }
      setUser(data);
      return true;
    } catch (e) {
      navigate('/login');
      return false;
    }
  }, [navigate]);

  const loadTenants = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/tenants');
      setTenants(res.data || []);
    } catch (e) {
      setError('Failed to load tenants');
    }
  }, []);

  const handleCreateTenant = async () => {
    if (!newTenant.tenant_code || !newTenant.domain || !newTenant.name) {
      setError('All fields are required');
      return;
    }
    setCreateLoading(true);
    setError('');
    try {
      await apiClient.post('/admin/tenants', newTenant);
      setNewTenant({ tenant_code: '', domain: '', name: '' });
      setShowCreateForm(false);
      await loadTenants();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreateLoading(false);
    }
  };

  // Aggregation Job Queue: fetch status
  const loadQueueStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/analyze/aggregation/queue/status');
      setQueueStatus(response.data);
      setMaxWorkers(response.data.max_workers);
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setError('Admin access required');
      } else {
        console.error('Failed to fetch queue status:', err);
      }
    }
  }, []);

  const reinitializeQueue = useCallback(async () => {
    if (actionLoading) return;
    setActionLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/analyze/aggregation/queue/reinit', { max_workers: maxWorkers });
      if (response.data.success) {
        setQueueStatus(response.data.status);
        setError(`✅ ${response.data.message}`);
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to update queue configuration';
      setError(`❌ ${errorMsg}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  }, [maxWorkers, actionLoading]);

  // Event Worker callbacks
  const loadEventWorkerData = useCallback(async () => {
    try {
      const [statusResponse, statsResponse] = await Promise.all([
        apiClient.get('/admin/event-worker/status'),
        apiClient.get('/admin/event-worker/statistics')
      ]);
      setEventWorkerStatus(statusResponse.data);
      setEventWorkerStats(statsResponse.data);
      if (statusResponse.data.worker_kwargs) {
        setEventWorkerConfig(prev => ({ ...prev, ...statusResponse.data.worker_kwargs }));
      }
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setError('Admin access required for event worker');
      } else {
        console.error('Failed to fetch event worker data:', err);
        setEventWorkerStatus({ is_running: false, error: true });
        setEventWorkerStats(null);
      }
    }
  }, []);

  const startEventWorker = useCallback(async () => {
    if (eventWorkerLoading) return;
    setEventWorkerLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/admin/event-worker/start', eventWorkerConfig);
      if (response.data.success) {
        setError(`✅ ${response.data.message}`);
        setTimeout(() => setError(''), 3000);
        await loadEventWorkerData();
      } else {
        setError(`❌ ${response.data.message}`);
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(`❌ ${err.response?.data?.detail || 'Failed to start event worker'}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setEventWorkerLoading(false);
    }
  }, [eventWorkerConfig, eventWorkerLoading, loadEventWorkerData]);

  const stopEventWorker = useCallback(async () => {
    if (eventWorkerLoading) return;
    setEventWorkerLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/admin/event-worker/stop');
      if (response.data.success) {
        setError(`✅ ${response.data.message}`);
        setTimeout(() => setError(''), 3000);
        await loadEventWorkerData();
      } else {
        setError(`❌ ${response.data.message}`);
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(`❌ ${err.response?.data?.detail || 'Failed to stop event worker'}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setEventWorkerLoading(false);
    }
  }, [eventWorkerLoading, loadEventWorkerData]);

  const restartEventWorker = useCallback(async () => {
    if (eventWorkerLoading) return;
    setEventWorkerLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/admin/event-worker/restart', eventWorkerConfig);
      if (response.data.success) {
        setError(`✅ ${response.data.message}`);
        setTimeout(() => setError(''), 3000);
        await loadEventWorkerData();
      } else {
        setError(`❌ ${response.data.message}`);
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(`❌ ${err.response?.data?.detail || 'Failed to restart event worker'}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setEventWorkerLoading(false);
    }
  }, [eventWorkerConfig, eventWorkerLoading, loadEventWorkerData]);

  // Memory Trace callbacks
  const loadMemoryTraceStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/admin/memory-trace/status');
      const status = response.data;
      setMemoryTraceStatus(status);
      if (status.results && status.results.length > 0) {
        setMemoryTraceResults(status.results);
      }
    } catch (err) {
      console.error('Failed to fetch memory trace status:', err);
    }
  }, []);

  const startMemoryTrace = useCallback(async () => {
    if (memoryTraceLoading) return;
    setMemoryTraceLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/admin/memory-trace/start', memoryTraceConfig);
      if (response.data.success) {
        setMemoryTraceResults([]);
        setError(`✅ ${response.data.message}`);
        setTimeout(() => setError(''), 3000);
        await loadMemoryTraceStatus();
      } else {
        setError(`❌ ${response.data.message}`);
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(`❌ ${err.response?.data?.detail || 'Failed to start memory tracing'}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setMemoryTraceLoading(false);
    }
  }, [memoryTraceLoading, memoryTraceConfig, loadMemoryTraceStatus]);

  const stopMemoryTrace = useCallback(async () => {
    if (memoryTraceLoading) return;
    setMemoryTraceLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/admin/memory-trace/stop');
      if (response.data.success) {
        setError(`✅ ${response.data.message}`);
        setTimeout(() => setError(''), 3000);
        setMemoryTraceResults(response.data.results || []);
        await loadMemoryTraceStatus();
      } else {
        setError(`❌ ${response.data.message}`);
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(`❌ ${err.response?.data?.detail || 'Failed to stop memory tracing'}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setMemoryTraceLoading(false);
    }
  }, [memoryTraceLoading, loadMemoryTraceStatus]);

  useEffect(() => {
    if (!memoryTraceStatus?.is_running) return;
    const interval = setInterval(() => {
      loadMemoryTraceStatus();
    }, memoryTraceConfig.polling_interval * 1000);
    return () => clearInterval(interval);
  }, [memoryTraceStatus?.is_running, loadMemoryTraceStatus, memoryTraceConfig.polling_interval]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const ok = await checkAuth();
      if (ok) {
        await Promise.all([loadTenants(), loadQueueStatus(), loadEventWorkerData(), loadMemoryTraceStatus()]);
      }
      setLoading(false);
    };
    init();
  }, [checkAuth, loadTenants, loadQueueStatus, loadEventWorkerData, loadMemoryTraceStatus]);

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const getQueueStatusBadge = () => {
    if (!queueStatus) return { text: 'Unknown', class: 'status-unknown' };
    return queueStatus.started ? { text: 'Running', class: 'status-running' } : { text: 'Stopped', class: 'status-stopped' };
  };

  const getEventWorkerStatusBadge = () => {
    if (!eventWorkerStatus) return { text: 'Unknown', class: 'status-unknown' };
    if (eventWorkerStatus.error) return { text: 'Error', class: 'status-unknown' };
    return eventWorkerStatus.is_running ? { text: 'Running', class: 'status-running' } : { text: 'Stopped', class: 'status-stopped' };
  };

  const formatFilePath = (filePath) => {
    if (!filePath) return 'Unknown';
    const parts = filePath.split('/');
    const relevantParts = parts.slice(-6);
    return relevantParts.join('/');
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading">Loading Super Admin...</div>
      </div>
    );
  }

  if (!user) { return null; }

  const queueStatusBadge = getQueueStatusBadge();
  const eventWorkerStatusBadge = getEventWorkerStatusBadge();

  return (
    <>
      <AdminWatermark />
      <div className="admin-container">
        <div className="admin-header">
          <div className="header-content">
            <div className="header-left">
              <h1>Super Admin</h1>
              <p>Manage tenants, users, aggregation job queue and event worker service</p>
            </div>
            <div className="header-right">
              <div className="user-info">
                <span className="user-name">{user.email}</span>
                <span className="tenant-badge admin-tenant">{user.tenant_code}</span>
                <span className="role-badge">{user.role}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-main">
          {error && (
            <div className="error-banner">
              <span>⚠️ {error}</span>
              <button onClick={() => setError('')}>×</button>
            </div>
          )}

          {/* Tenants Table */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Tenants</h2>
              <div className="card-header-actions">
                <button className="refresh-btn" onClick={loadTenants}>🔄 Refresh</button>
                <button
                  className="action-btn start-btn"
                  onClick={() => setShowCreateForm(!showCreateForm)}
                  style={{ minWidth: 'auto', padding: '0.5rem 1rem' }}
                >
                  {showCreateForm ? '✕ Cancel' : '+ New Tenant'}
                </button>
              </div>
            </div>

            {showCreateForm && (
              <div className="config-form" style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f7fafc', borderRadius: '8px' }}>
                <h3>Create New Tenant</h3>
                <div className="config-grid">
                  <div className="config-item">
                    <label>Tenant Code</label>
                    <input
                      type="text"
                      placeholder="e.g. acme"
                      value={newTenant.tenant_code}
                      onChange={e => setNewTenant(prev => ({ ...prev, tenant_code: e.target.value }))}
                      disabled={createLoading}
                    />
                  </div>
                  <div className="config-item">
                    <label>Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Acme Corp"
                      value={newTenant.name}
                      onChange={e => setNewTenant(prev => ({ ...prev, name: e.target.value }))}
                      disabled={createLoading}
                    />
                  </div>
                  <div className="config-item">
                    <label>Domain</label>
                    <input
                      type="text"
                      placeholder="e.g. acme.com"
                      value={newTenant.domain}
                      onChange={e => setNewTenant(prev => ({ ...prev, domain: e.target.value }))}
                      disabled={createLoading}
                    />
                  </div>
                </div>
                <div className="action-buttons" style={{ marginTop: '1rem' }}>
                  <button
                    className="action-btn start-btn"
                    onClick={handleCreateTenant}
                    disabled={createLoading}
                  >
                    {createLoading ? '⏳ Creating...' : '✓ Create Tenant'}
                  </button>
                </div>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Tenant Code</th>
                    <th>Domain</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', color: '#a0aec0', padding: '2rem' }}>
                        No tenants found
                      </td>
                    </tr>
                  ) : (
                    tenants.map(t => (
                      <tr
                        key={t._id}
                        className="clickable-row"
                        onClick={() => navigate(`/admin/super/tenants/${t._id}`)}
                      >
                        <td style={{ fontWeight: 500 }}>{t.name}</td>
                        <td>
                          <span className="tenant-badge" style={{ fontSize: '0.8rem' }}>{t.tenant_code}</span>
                        </td>
                        <td>{t.domain}</td>
                        <td>
                          <span className={`active-badge ${t.is_active !== false ? 'active' : 'inactive'}`}>
                            {t.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Aggregation Job Queue Section */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Aggregation Job Queue</h2>
              <div className="card-header-actions">
                <button className="refresh-btn" onClick={loadQueueStatus} disabled={actionLoading}>🔄 Refresh</button>
              </div>
            </div>

            <div className="status-grid">
              <div className="status-item">
                <label>Status</label>
                <span className={`status-badge ${queueStatusBadge.class}`}>{queueStatusBadge.text}</span>
              </div>
              <div className="status-item">
                <label>Max Workers</label>
                <span>{queueStatus?.max_workers || 'N/A'}</span>
              </div>
              <div className="status-item">
                <label>Active Workers</label>
                <span style={{ color: queueStatus?.active_workers > 0 ? '#1890ff' : '#999' }}>
                  {queueStatus?.active_workers || 0}
                </span>
              </div>
              <div className="status-item">
                <label>Running Jobs</label>
                <span style={{ color: queueStatus?.running_jobs > 0 ? '#52c41a' : '#999' }}>
                  {queueStatus?.running_jobs || 0}
                </span>
              </div>
              <div className="status-item">
                <label>Queued Jobs</label>
                <span style={{ color: queueStatus?.queued_jobs > 0 ? '#fa8c16' : '#999' }}>
                  {queueStatus?.queued_jobs || 0}
                </span>
              </div>
            </div>

            {queueStatus?.running_job_ids && queueStatus.running_job_ids.length > 0 && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Currently Executing:</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                  {queueStatus.running_job_ids.join(', ')}
                </div>
              </div>
            )}

            <div className="config-form" style={{ marginTop: '1rem' }}>
              <h3>Configuration</h3>
              <div className="config-grid">
                <div className="config-item">
                  <label>Max Workers</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={maxWorkers}
                    onChange={(e) => setMaxWorkers(parseInt(e.target.value || '1', 10))}
                    disabled={actionLoading}
                  />
                  <small style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                    Number of concurrent aggregation jobs across all tenants
                  </small>
                </div>
              </div>
            </div>

            <div className="action-buttons">
              <button className="action-btn restart-btn" onClick={reinitializeQueue} disabled={actionLoading}>
                {actionLoading ? '⏳' : '⚙️'} Update Configuration
              </button>
            </div>
          </div>

          {/* Event Worker Section */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Event Worker Service</h2>
              <div className="card-header-actions">
                <button className="refresh-btn" onClick={loadEventWorkerData} disabled={eventWorkerLoading}>🔄 Refresh</button>
              </div>
            </div>

            <div className="status-grid">
              <div className="status-item">
                <label>Status</label>
                <span className={`status-badge ${eventWorkerStatusBadge.class}`}>{eventWorkerStatusBadge.text}</span>
              </div>
              <div className="status-item">
                <label>Uptime</label>
                <span>{formatUptime(eventWorkerStatus?.uptime_seconds)}</span>
              </div>
              <div className="status-item">
                <label>Batch Size</label>
                <span>{eventWorkerStatus?.worker_kwargs?.batch_size || eventWorkerConfig.batch_size}</span>
              </div>
              <div className="status-item">
                <label>Polling Interval</label>
                <span>{eventWorkerStatus?.worker_kwargs?.polling_interval || eventWorkerConfig.polling_interval}s</span>
              </div>
              <div className="status-item">
                <label>Retry Delay</label>
                <span>{eventWorkerStatus?.worker_kwargs?.retry_delay_minutes || eventWorkerConfig.retry_delay_minutes}m</span>
              </div>
            </div>

            {eventWorkerStats && (
              <div style={{ marginTop: '1rem' }}>
                <h3>Processing Statistics</h3>
                <div className="status-grid">
                  <div className="status-item">
                    <label>Total Events</label>
                    <span style={{ fontWeight: 'bold' }}>{eventWorkerStats.total_events}</span>
                  </div>
                  <div className="status-item">
                    <label>Completed</label>
                    <span style={{ color: '#52c41a' }}>{eventWorkerStats.done}</span>
                  </div>
                  <div className="status-item">
                    <label>Failed</label>
                    <span style={{ color: eventWorkerStats.failed > 0 ? '#ff4d4f' : '#999' }}>{eventWorkerStats.failed}</span>
                  </div>
                  <div className="status-item">
                    <label>Abandoned</label>
                    <span style={{ color: eventWorkerStats.abandoned > 0 ? '#fa8c16' : '#999' }}>{eventWorkerStats.abandoned}</span>
                  </div>
                  <div className="status-item">
                    <label>Unprocessed</label>
                    <span style={{ color: eventWorkerStats.unprocessed > 0 ? '#1890ff' : '#999' }}>{eventWorkerStats.unprocessed}</span>
                  </div>
                  <div className="status-item">
                    <label>Retry Ready</label>
                    <span style={{ color: eventWorkerStats.retry_ready > 0 ? '#fa8c16' : '#999' }}>{eventWorkerStats.retry_ready}</span>
                  </div>
                </div>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    Processing Progress: {eventWorkerStats.total_events > 0 ? Math.round((eventWorkerStats.done / eventWorkerStats.total_events) * 100) : 0}%
                  </div>
                  <div style={{ width: '100%', height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${eventWorkerStats.total_events > 0 ? (eventWorkerStats.done / eventWorkerStats.total_events) * 100 : 0}%`,
                        height: '100%',
                        backgroundColor: '#52c41a',
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="config-form" style={{ marginTop: '1rem' }}>
              <h3>Configuration</h3>
              <div className="config-grid">
                <div className="config-item">
                  <label>Batch Size</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={eventWorkerConfig.batch_size}
                    onChange={(e) => setEventWorkerConfig(prev => ({ ...prev, batch_size: parseInt(e.target.value || '1', 10) }))}
                    disabled={eventWorkerLoading}
                  />
                  <small style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                    Number of events to process in each batch (1-100)
                  </small>
                </div>
                <div className="config-item">
                  <label>Polling Interval (s)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={eventWorkerConfig.polling_interval}
                    onChange={(e) => setEventWorkerConfig(prev => ({ ...prev, polling_interval: parseInt(e.target.value || '1', 10) }))}
                    disabled={eventWorkerLoading}
                  />
                  <small style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                    How often to check for new events (1-60 seconds)
                  </small>
                </div>
                <div className="config-item">
                  <label>Retry Delay (min)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={eventWorkerConfig.retry_delay_minutes}
                    onChange={(e) => setEventWorkerConfig(prev => ({ ...prev, retry_delay_minutes: parseInt(e.target.value || '1', 10) }))}
                    disabled={eventWorkerLoading}
                  />
                  <small style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                    Wait time before retrying failed events (1-60 minutes)
                  </small>
                </div>
              </div>
            </div>

            <div className="action-buttons">
              {eventWorkerStatus?.is_running ? (
                <>
                  <button className="action-btn stop-btn" onClick={stopEventWorker} disabled={eventWorkerLoading}>
                    {eventWorkerLoading ? '⏳' : '⏹️'} Stop Worker
                  </button>
                  <button className="action-btn restart-btn" onClick={restartEventWorker} disabled={eventWorkerLoading}>
                    {eventWorkerLoading ? '⏳' : '🔄'} Restart Worker
                  </button>
                </>
              ) : (
                <button className="action-btn start-btn" onClick={startEventWorker} disabled={eventWorkerLoading}>
                  {eventWorkerLoading ? '⏳' : '▶️'} Start Worker
                </button>
              )}
            </div>
          </div>

          {/* Memory Allocation Investigation Section */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Memory Allocation Investigation</h2>
              <div className="card-header-actions">
                <button className="refresh-btn" onClick={loadMemoryTraceStatus} disabled={memoryTraceLoading}>🔄 Refresh</button>
              </div>
            </div>

            <div className="status-grid">
              <div className="status-item">
                <label>Status</label>
                <span className={`status-badge ${memoryTraceStatus?.is_running ? 'status-running' : 'status-stopped'}`}>
                  {memoryTraceStatus?.is_running ? 'Tracing' : memoryTraceStatus?.threshold_reached ? 'Threshold Reached' : 'Stopped'}
                </span>
              </div>
              <div className="status-item">
                <label>Snapshots</label>
                <span>{memoryTraceStatus?.snapshots_taken || 0}</span>
              </div>
              <div className="status-item">
                <label>Elapsed</label>
                <span>{memoryTraceStatus?.elapsed_seconds ? `${memoryTraceStatus.elapsed_seconds}s` : 'N/A'}</span>
              </div>
              <div className="status-item">
                <label>Initial Size</label>
                <span>{memoryTraceStatus?.initial_size_human || 'N/A'}</span>
              </div>
              <div className="status-item">
                <label>Current Size</label>
                <span>{memoryTraceStatus?.current_size_human || 'N/A'}</span>
              </div>
            </div>

            {memoryTraceStatus?.is_running && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#e6f7ff', borderRadius: '4px', border: '1px solid #91d5ff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                    Memory Increase: +{memoryTraceStatus.current_increase_mb} MB
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#666' }}>
                    Threshold: +{memoryTraceStatus.threshold_mb} MB
                  </span>
                </div>
                <div style={{ width: '100%', height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${Math.min((memoryTraceStatus.current_increase_mb / memoryTraceStatus.threshold_mb) * 100, 100)}%`,
                      height: '100%',
                      backgroundColor: memoryTraceStatus.current_increase_mb >= memoryTraceStatus.threshold_mb * 0.8 ? '#fa8c16' : '#1890ff',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                  Polling every {memoryTraceStatus.polling_interval}s • Auto-stops at +{memoryTraceStatus.threshold_mb} MB increase
                </div>
              </div>
            )}

            {memoryTraceStatus?.threshold_reached && !memoryTraceStatus?.is_running && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fff2e8', borderRadius: '4px', border: '1px solid #ffbb96' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#d46b08' }}>
                  ⚠️ Threshold Reached: +{memoryTraceStatus.current_increase_mb} MB memory increase detected
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  Tracing automatically stopped after {memoryTraceStatus.snapshots_taken} snapshots
                </div>
              </div>
            )}

            {!memoryTraceStatus?.is_running && (
              <div className="config-form" style={{ marginTop: '1rem' }}>
                <h3>Configuration</h3>
                <div className="config-grid">
                  <div className="config-item">
                    <label>Threshold (MB)</label>
                    <input
                      type="number"
                      min="0.1"
                      max="10000"
                      step="0.1"
                      value={memoryTraceConfig.threshold_mb}
                      onChange={(e) => setMemoryTraceConfig(prev => ({ ...prev, threshold_mb: parseFloat(e.target.value || '10') }))}
                      disabled={memoryTraceLoading}
                    />
                    <small style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                      Auto-stop when memory increases by this amount (in megabytes)
                    </small>
                  </div>
                  <div className="config-item">
                    <label>Polling Interval (s)</label>
                    <input
                      type="number"
                      min="1"
                      max="300"
                      value={memoryTraceConfig.polling_interval}
                      onChange={(e) => setMemoryTraceConfig(prev => ({ ...prev, polling_interval: parseInt(e.target.value || '5', 10) }))}
                      disabled={memoryTraceLoading}
                    />
                    <small style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                      How often to take snapshots and check threshold (1-300 seconds)
                    </small>
                  </div>
                </div>
              </div>
            )}

            {!memoryTraceStatus?.is_running && !memoryTraceStatus?.threshold_reached && memoryTraceResults.length === 0 && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>How to use:</div>
                <ol style={{ fontSize: '0.85rem', color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
                  <li>Configure threshold (in MB) and polling interval</li>
                  <li>Click "Start" to begin automatic memory tracing</li>
                  <li>Tracing will auto-stop when memory increases by the threshold MB</li>
                  <li>Or click "Stop" manually to get results at any time</li>
                </ol>
              </div>
            )}

            <div className="action-buttons" style={{ marginTop: '1rem' }}>
              {memoryTraceStatus?.is_running ? (
                <button className="action-btn stop-btn" onClick={stopMemoryTrace} disabled={memoryTraceLoading}>
                  {memoryTraceLoading ? '⏳' : '⏹️'} Stop & Get Results
                </button>
              ) : (
                <button className="action-btn start-btn" onClick={startMemoryTrace} disabled={memoryTraceLoading}>
                  {memoryTraceLoading ? '⏳' : '▶️'} Start Tracing
                </button>
              )}
            </div>

            {memoryTraceResults.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <h3>Memory Allocation Differences (Top {memoryTraceResults.length})</h3>
                <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>File / Location</th>
                        <th style={{ textAlign: 'right' }}>Size Diff</th>
                        <th style={{ textAlign: 'right' }}>Total Size</th>
                        <th style={{ textAlign: 'right' }}>Count Diff</th>
                        <th style={{ textAlign: 'right' }}>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {memoryTraceResults.map((stat, idx) => (
                        <tr
                          key={idx}
                          onClick={() => stat.traceback && setSelectedTraceback(stat.traceback)}
                          style={{ cursor: stat.traceback ? 'pointer' : 'default' }}
                          title={stat.traceback ? 'Click to view stack trace' : ''}
                        >
                          <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', whiteSpace: 'normal' }}>
                            {formatFilePath(stat.file)}
                          </td>
                          <td style={{
                            textAlign: 'right', fontFamily: 'monospace',
                            color: stat.size_diff > 0 ? '#ff4d4f' : stat.size_diff < 0 ? '#52c41a' : '#999'
                          }}>
                            {stat.size_diff_human}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{stat.size_human}</td>
                          <td style={{
                            textAlign: 'right', fontFamily: 'monospace',
                            color: stat.count_diff > 0 ? '#ff4d4f' : stat.count_diff < 0 ? '#52c41a' : '#999'
                          }}>
                            {stat.count_diff > 0 ? '+' : ''}{stat.count_diff}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{stat.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                  <strong>Legend:</strong>
                  <span style={{ color: '#ff4d4f', marginLeft: '0.5rem' }}>Red = Memory increased</span>
                  <span style={{ color: '#52c41a', marginLeft: '0.5rem' }}>Green = Memory decreased</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Traceback Modal */}
      {selectedTraceback && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setSelectedTraceback(null)}
        >
          <div
            style={{
              backgroundColor: '#fff', borderRadius: '8px', padding: '1.5rem',
              width: '95%', maxWidth: '1200px', height: '85vh',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Stack Trace</h3>
              <button
                onClick={() => setSelectedTraceback(null)}
                style={{
                  background: 'none', border: 'none', fontSize: '1.5rem',
                  cursor: 'pointer', color: '#666', padding: '0.25rem 0.5rem', lineHeight: 1
                }}
              >
                ×
              </button>
            </div>
            <textarea
              readOnly
              value={selectedTraceback.join('\n')}
              style={{
                flex: 1, fontFamily: 'monospace', fontSize: '0.85rem', padding: '1rem',
                border: '1px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#f5f5f5',
                resize: 'none', lineHeight: 1.6, overflow: 'auto'
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SuperAdminTenantManagementPage;
